import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import {
  BehaviorSubject,
  forkJoin,
  from,
  Observable,
  of,
  throwError,
} from "rxjs";
import {
  catchError,
  concatMap,
  map,
  switchMap,
  tap,
  toArray,
} from "rxjs/operators";

import { DialogServiceService } from "src/app/_app-core/dialogs/dialog-service.service";
import {
  attachTypeForDocumentGroup,
  attachmentTypeForFile,
  fileExtension,
  fileToBase64,
} from "src/app/_pages/supplier/change-requests/change-request-attachment-mapping";
import { Config } from "src/app/_store/config";

/**
 * Row shape for the Change Requests data API response (apiDataArray entry).
 * Only fields rendered on the page are typed; the rest are accessible via the index signature.
 */
export interface ChangeRequestRow {
  MDRM_INPROCESS_REQ_NO?: string;
  MDRM_RECORD_NO?: string;
  VENDOR_NAME?: string;
  REQUEST_CATEGORY?: string;
  REQUEST_DESCR?: string | null;
  REQUEST_APPROVER?: string;
  STATUS?: string;
  STATUS_DESCRIPTION?: string;
  [k: string]: unknown;
}

export interface ChangeRequestAttachmentRef {
  groupId: string;
  file: File;
}

export interface SubmitChangeRequestPayload {
  editedFields: string[];
  stgColumn691: Record<string, unknown>;
  updatedFields: Record<string, unknown>;
  vendorRow: Record<string, unknown>;
  requestorRow: Record<string, unknown>;
  submitMode?: "insert" | "update";
  /** When resubmitting, reuse this `STG_NO` instead of generating a new one. */
  reuseStgNo?: string;
  /** Supporting documents to upload after the main CR payload succeeds. */
  attachments?: ChangeRequestAttachmentRef[];
  /** Full filenames for Mongo audit (not sent to JSW). */
  uploadedDocumentNames?: string[];
}

export interface SubmitChangeRequestResponse {
  stgNo: string;
  jswResponse: unknown;
  mirrored: boolean;
}

/**
 * Row shape returned by the backend `jswVMD/list` endpoint (one document per
 * change request stored in the `jswVMDQueries` collection).
 */
export interface ChangeRequestDbRow {
  _id: string;
  recordNo: string | null;
  createdAt: string | Date;
  status: string;
  /** JSW `COMMENTS` from the change-request status API, persisted on refresh. */
  statusComments?: string | null;
  /** JSW `MDRM_CHANGE_REQ_NUMBER` from the crStatus API, persisted on refresh. */
  mdrmInProcessReqNo?: string | null;
  stgColumn691: Record<string, unknown>;
  updatedFields?: Record<string, unknown>;
  uploadedDocumentNames?: string[];
}

/** One page from `jswVMD/pending` (paginated). */
export interface PendingChangeRequestsPage {
  list: Array<{ _id: string; status: string }>;
  noOfRecords: number;
}

/** One page from `jswVMD/list` (paginated). */
export interface ChangeRequestsListPage {
  list: ChangeRequestDbRow[];
  noOfRecords: number;
}

/**
 * JSW vendor master data (VMD) service. All JSW aadhaar.jsw.in gateway calls
 * are made server-to-server by the backend `/jswVMD/gateway/*` endpoints.
 */
@Injectable({
  providedIn: "root",
})
export class JswVmdService {
  /** Status value that means "no further polling needed" for a CR. */
  private static readonly TERMINAL_STATUS = "REQUEST ACCEPTED BY ERP SUCESSFULLY";
  private static readonly STG_NO_PREFIX = "VENDX-";
  /** Default ORGN_ID sent in the create-new and edit-request payloads. */
  private static readonly DEFAULT_ORGN_ID = "961E7EAD08C940F3E0532B0110ACE32E";
  /** API 2 response: Vendor Details */
  static readonly VENDOR_DETAILS_KEY = "jswAadhaarVendorDetails";
  /** API 3 response: Requestor Details */
  static readonly REQUESTOR_DETAILS_KEY = "jswAadhaarRequestorDetails";
  /** Mongo / JSW status for a rejected CR that may be edited and resubmitted. */
  static readonly CR_STATUS_REQUEST_REJECTED = "REQUEST REJECTED";
  static readonly CR_STATUS_AWAITING_JSW = "AWAITING JSW STATUS";
  static readonly CR_STATUS_SUBMITTED_BY_VENDOR = "REQUEST SUBMITTED BY VENDOR";
  static readonly CR_STATUS_RESUBMITTED_BY_VENDOR =
    "REQUEST RE-SUBMITTED BY VENDOR";
  /** DB statuses that block create-new / edit-rejected actions on the list page. */
  static readonly CR_BLOCKING_DB_STATUSES: ReadonlySet<string> = new Set([
    "AWAITING JSW STATUS",
    "REQUEST SUBMITTED BY VENDOR",
    "REQUEST RE-SUBMITTED BY VENDOR",
  ]);

  /**
   * In-memory flag that signals to consumers (e.g. /change-requests pages)
   * that the JSW access token is unusable. Components subscribe to it to
   * gate their UI behind a "re-authenticate" banner. The flag is reset on
   * login, on successful re-auth, and on logout.
   */
  private readonly jswAuthFailedSubject = new BehaviorSubject<boolean>(false);
  readonly jswAuthFailed$: Observable<boolean> =
    this.jswAuthFailedSubject.asObservable();

  constructor(
    private appHttp: HttpClient,
    private dialogs: DialogServiceService,
  ) {}

  get isJswAuthFailed(): boolean {
    return this.jswAuthFailedSubject.value;
  }

  private isJswAuthError(err: unknown): boolean {
    return (err as any)?.status === 401 || (err as any)?.status === 403;
  }

  private markJswAuthFailed(): void {
    sessionStorage.removeItem(JswVmdService.VENDOR_DETAILS_KEY);
    sessionStorage.removeItem(JswVmdService.REQUESTOR_DETAILS_KEY);
    if (!this.jswAuthFailedSubject.value) {
      this.jswAuthFailedSubject.next(true);
    }
  }

  clearJswAuthFailed(): void {
    if (this.jswAuthFailedSubject.value) {
      this.jswAuthFailedSubject.next(false);
    }
  }

  clearStoredVendorData(): void {
    sessionStorage.removeItem(JswVmdService.VENDOR_DETAILS_KEY);
    sessionStorage.removeItem(JswVmdService.REQUESTOR_DETAILS_KEY);
  }

  /**
   * Post-login: fetches Vendor + Requestor details via backend gateway and
   * stores each response in sessionStorage. On auth error, flags the banner.
   * Resolves to `of(null)` on failure so login navigation proceeds.
   */
  fetchAndStoreForVendor(sapVendorCode: string): Observable<unknown> {
    const code = (sapVendorCode || "").trim();
    if (!code) {
      console.warn("JswVmdService.fetchAndStoreForVendor: empty sapVendorCode");
      return of(null);
    }
    return forkJoin({
      vendor: this.appHttp.post<{ data?: unknown }>(Config.urls.JSW_GATEWAY_VENDOR_DETAILS, { sapVendorCode: code }),
      requestor: this.appHttp.post<{ data?: unknown }>(Config.urls.JSW_GATEWAY_REQUESTOR_DETAILS, { sapVendorCode: code }),
    }).pipe(
      tap(({ vendor, requestor }) => {
        sessionStorage.setItem(JswVmdService.VENDOR_DETAILS_KEY, JSON.stringify(vendor?.data ?? vendor));
        sessionStorage.setItem(JswVmdService.REQUESTOR_DETAILS_KEY, JSON.stringify(requestor?.data ?? requestor));
      }),
      catchError((err) => {
        console.error("JswVmdService fetch failed", err);
        if (this.isJswAuthError(err)) { this.markJswAuthFailed(); }
        return of(null);
      }),
    );
  }

  /**
   * User-initiated re-authentication. Replays the post-login sequence:
   *   1. apiAuthToken (fresh)
   *   2. getApiRequestResultsData -- Vendor Details
   *   3. getApiRequestResultsData -- Requestor Details
   *
   * Uses default `forkJoin` semantics so a failure in EITHER details call
   * aborts the whole flow -- matching the "all 3 must succeed" requirement.
   * On all-success the new token + payloads are cached and `jswAuthFailed$`
   * is cleared so the banner disappears and gated pages re-load. On any
   * failure the flag stays set (the inner catchErrors flip it back) and the
   * observable errors so the calling component can surface the
   * "Re-authentication failed" modal.
   */
  reauthenticate(sapVendorCode: string): Observable<void> {
    const code = (sapVendorCode || "").trim();
    if (!code) {
      return throwError(() => new Error("vendor code is required for re-auth"));
    }
    this.clearJswAuthFailed();
    return this.appHttp.post(Config.urls.JSW_GATEWAY_AUTH_TOKEN, {}).pipe(
      switchMap(() =>
        forkJoin({
          vendor: this.appHttp.post<{ data?: unknown }>(Config.urls.JSW_GATEWAY_VENDOR_DETAILS, { sapVendorCode: code }),
          requestor: this.appHttp.post<{ data?: unknown }>(Config.urls.JSW_GATEWAY_REQUESTOR_DETAILS, { sapVendorCode: code }),
        }),
      ),
      tap(({ vendor, requestor }) => {
        sessionStorage.setItem(JswVmdService.VENDOR_DETAILS_KEY, JSON.stringify(vendor?.data ?? vendor));
        sessionStorage.setItem(JswVmdService.REQUESTOR_DETAILS_KEY, JSON.stringify(requestor?.data ?? requestor));
      }),
      map(() => void 0),
      catchError((err) => {
        this.markJswAuthFailed();
        return throwError(() => err);
      }),
    );
  }

  /**
   * Fetches the in-process Change Requests for the given SAP vendor code.
   * Reuses the cached access_token; only calls apiAuthToken on cache miss or after a 401/403
   * (in which case the cache is invalidated and a single auth-then-retry is performed).
   */
  fetchChangeRequests(sapVendorCode: string): Observable<ChangeRequestRow[]> {
    const code = (sapVendorCode || "").trim();
    if (!code) {
      return of([]);
    }
    return this.appHttp.post(Config.urls.JSW_GATEWAY_CHANGE_REQUESTS, { sapVendorCode: code }).pipe(
      map((resp: unknown) => {
        const r = resp as { data?: { apiDataArray?: unknown } } | null;
        const data = (r as any)?.data ?? r;
        const arr = data && Array.isArray((data as any).apiDataArray) ? (data as any).apiDataArray : [];
        return arr as ChangeRequestRow[];
      }),
      catchError((err) => {
        if (this.isJswAuthError(err)) { this.markJswAuthFailed(); }
        throw err;
      }),
    );
  }

  /**
   * Submits a new vendor change request:
   *  1. Uses a unique STG_NO (`generateStgNo`) for new requests, or the
   *     caller-supplied id when resubmitting (`reuseStgNo` + update mode).
   *  2. POSTs the curl payload directly to `updateInsertDeleteApiRequestData`
   *     (with cached-token + 401/403 re-auth retry, same as `fetchChangeRequests`)
   *  3. On JSW success, mirrors the payload + JSW response to the backend
   *     `jswVMD/changeRequest` endpoint which persists into `jswVMDQueries`.
   *
   * Mirror failures do not throw; they are surfaced via a non-blocking modal
   * and reflected in the returned `mirrored: false` flag so the caller can
   * inform the user that the JSW request landed but the audit row didn't.
   */
  submitChangeRequest(
    payload: SubmitChangeRequestPayload,
  ): Observable<SubmitChangeRequestResponse> {
    const updatedFields = payload.updatedFields || {};
    const uploadedDocumentNames = payload.uploadedDocumentNames || [];
    const attachments = payload.attachments || [];
    const reuse = (payload.reuseStgNo || "").trim();
    if (payload.submitMode === "update" && !reuse) {
      return throwError(() => ({
        userMessage:
          "Change request reference ID is missing. Cannot resubmit or upload documents.",
      }));
    }
    const stgNo$ =
      payload.submitMode === "update"
        ? of(reuse)
        : from(this.generateStgNo());

    return stgNo$.pipe(
      switchMap((stgNo) => {
        const body = this.buildUpdatePayload(stgNo, payload);
        return this.appHttp.post(Config.urls.JSW_GATEWAY_SUBMIT_CR, { payload: body }).pipe(
          map((jswResponse: unknown) => ({ stgNo, body, jswResponse })),
          switchMap(({ stgNo: id, body: crBody, jswResponse }) =>
            this.uploadAttachments(id, attachments, payload).pipe(
              map(() => ({ stgNo: id, body: crBody, jswResponse })),
              catchError((err) =>
                throwError(() => ({
                  ...(typeof err === "object" && err !== null ? err : {}),
                  stgNo: id,
                  userMessage:
                    `Change request ${id} was submitted to JSW, but uploading supporting documents failed. Reference: ${id}. Please try again or contact support.`,
                })),
              ),
            ),
          ),
          switchMap(({ stgNo: id, body: crBody, jswResponse }) =>
            this.mirrorChangeRequest(
              id,
              crBody,
              jswResponse,
              updatedFields,
              uploadedDocumentNames,
            ).pipe(
              map((mirrored) => ({ stgNo: id, jswResponse, mirrored })),
            ),
          ),
        );
      }),
    );
  }

  private uploadAttachments(
    stgNo: string,
    attachments: ChangeRequestAttachmentRef[],
    payload: SubmitChangeRequestPayload,
  ): Observable<void> {
    const items = attachments.filter((a) => {
      const attachType = attachTypeForDocumentGroup(a.groupId);
      return attachType && a.file;
    });
    if (items.length === 0) {
      return of(undefined);
    }
    const apiReqUserId = this.resolveRequestorId(payload.requestorRow);
    return from(items).pipe(
      concatMap((item) => {
        const attachType = attachTypeForDocumentGroup(item.groupId)!;
        const ext = fileExtension(item.file.name);
        return from(fileToBase64(item.file)).pipe(
          switchMap((content) => {
            const attachBody = this.buildAttachmentPayload(
              stgNo,
              item.file.name,
              ext,
              content,
              attachType,
              apiReqUserId,
            );
            return this.appHttp.post(Config.urls.JSW_GATEWAY_UPLOAD_ATTACHMENT, { payload: attachBody });
          }),
        );
      }),
      toArray(),
      map(() => undefined),
    );
  }

  private buildAttachmentPayload(
    stgNo: string,
    fileName: string,
    ext: string,
    contentBase64: string,
    attachType: string,
    apiReqUserId: string,
  ): Record<string, unknown> {
    const apiReqId = Config.jswVmdApiReqIds.API_REQ_ID_ATTACHMENT;
    return {
      apiReqId,
      apiInsertFlag: "Y",
      apiAttachFlag: "Y",
      apiReqUserId,
      [apiReqId]: [
        {
          VENDOR_ID: stgNo,
          REGION: "IN",
          LOCALE: "en_US",
          DEFAULT_FLAG: "N",
          TYPE: attachmentTypeForFile(ext),
          ATTACH_TYPE: attachType,
          ACTIVE_FLAG: "Y",
          FILE_NAME: fileName,
          ATTACH_EXTENSION: ext,
          CONTENT: contentBase64,
        },
      ],
    };
  }

  /**
   * Fetches one page of change-request rows from `jswVMD/list` (Mongo
   * `jswVMDQueries`). Used to render the /change-requests history table.
   */
  listChangeRequestsFromDb(
    vendorCode: string,
    pageNo = 1,
    pageSize = Config.pageSize,
  ): Observable<ChangeRequestsListPage> {
    const code = (vendorCode || "").trim();
    if (!code) {
      return of({ list: [], noOfRecords: 0 });
    }
    let size = Number(pageSize);
    if (Number.isNaN(size) || size < 1) {
      size = Config.pageSize;
    }
    if (size > 100) {
      size = 100;
    }
    let page = Number(pageNo);
    if (Number.isNaN(page) || page < 1) {
      page = 1;
    }
    return this.appHttp
      .post<{ data?: ChangeRequestsListPage | ChangeRequestDbRow[] }>(
        Config.urls.LIST_CHANGE_REQUESTS,
        { vendorCode: code, pageNo: page, pageSize: size },
      )
      .pipe(
        map((resp) => {
          const d = resp && resp.data;
          if (Array.isArray(d)) {
            return { list: d, noOfRecords: d.length };
          }
          if (d && typeof d === "object" && Array.isArray((d as ChangeRequestsListPage).list)) {
            const p = d as ChangeRequestsListPage;
            return { list: p.list, noOfRecords: Number(p.noOfRecords) || 0 };
          }
          return { list: [], noOfRecords: 0 };
        }),
      );
  }

  /**
   * Fetches a single change request by `_id` and vendor code for resubmit
   * prefill (`updatedFields`, `status`). POST `jswVMD/detail`.
   */
  getChangeRequestDetailFromDb(
    vendorCode: string,
    stgNo: string,
  ): Observable<Pick<
    ChangeRequestDbRow,
    "_id" | "status" | "updatedFields"
  > | null> {
    const v = (vendorCode || "").trim();
    const id = (stgNo || "").trim();
    if (!v || !id) {
      return of(null);
    }
    return this.appHttp
      .post<{
        data?: Pick<ChangeRequestDbRow, "_id" | "status" | "updatedFields">;
      }>(Config.urls.GET_CHANGE_REQUEST_DETAIL, {
        vendorCode: v,
        stgNo: id,
      })
      .pipe(
        map((resp) =>
          resp && resp.data && typeof resp.data === "object" ? resp.data : null,
        ),
      );
  }

  /**
   * Returns one page of CRs that still need a status refresh (not REQUEST ACCEPTED BY ERP SUCESSFULLY
   * or REJECTED; see `jswVMDService` pending query).
   * Backed by `jswVMD/pending` with `pageNo` / `pageSize` (Mongo `skip` / `limit`).
   * Response shape: `{ list, noOfRecords }`.
   */
  listPendingChangeRequests(
    vendorCode: string,
    pageNo: number = 1,
    pageSize: number = Config.pageSize,
  ): Observable<PendingChangeRequestsPage> {
    const code = (vendorCode || "").trim();
    if (!code) {
      return of({ list: [], noOfRecords: 0 });
    }
    const pn = Number.isFinite(pageNo) && pageNo >= 1 ? Math.floor(pageNo) : 1;
    const ps =
      Number.isFinite(pageSize) && pageSize >= 1
        ? Math.min(100, Math.floor(pageSize))
        : Config.pageSize;
    return this.appHttp
      .post<{ data?: PendingChangeRequestsPage }>(
        Config.urls.LIST_PENDING_CHANGE_REQUESTS,
        { vendorCode: code, pageNo: pn, pageSize: ps },
      )
      .pipe(
        map((resp) => {
          const d = resp && resp.data;
          if (!d || typeof d !== "object") {
            return { list: [], noOfRecords: 0 };
          }
          const list = Array.isArray(d.list) ? d.list : [];
          const noOfRecords =
            typeof d.noOfRecords === "number" && d.noOfRecords >= 0
              ? d.noOfRecords
              : list.length;
          return { list, noOfRecords };
        }),
      );
  }

  /**
   * Loads every pending CR row for the vendor by paging `listPendingChangeRequests`
   * until all `noOfRecords` are fetched.
   */
  fetchAllPendingChangeRequestRows(
    vendorCode: string,
  ): Observable<Array<{ _id: string; status: string }>> {
    const pageSize = Config.pageSize;
    const loadPage = (
      pageNo: number,
      acc: Array<{ _id: string; status: string }>,
    ): Observable<Array<{ _id: string; status: string }>> => {
      return this.listPendingChangeRequests(vendorCode, pageNo, pageSize).pipe(
        switchMap((page) => {
          const merged = acc.concat(page.list);
          if (
            page.list.length < pageSize ||
            merged.length >= page.noOfRecords
          ) {
            return of(merged);
          }
          return loadPage(pageNo + 1, merged);
        }),
      );
    };
    return loadPage(1, []);
  }

  /**
   * Persists a refreshed JSW status for the given STG_NO (Mongo `_id`).
   * Failures are surfaced via the caller's `catchError` so they don't break
   * the orchestration loop.
   */
  updateChangeRequestStatusInDb(
    stgNo: string,
    status: string,
    comments?: string | null,
    mdrmInProcessReqNo?: string | null,
  ): Observable<unknown> {
    return this.appHttp.post(Config.urls.UPDATE_CHANGE_REQUEST_STATUS, {
      stgNo,
      status,
      comments: comments == null ? null : String(comments),
      mdrmInProcessReqNo:
        mdrmInProcessReqNo == null ? null : String(mdrmInProcessReqNo),
    });
  }

  private fetchChangeRequestStatusFromJsw(
    stgNo: string,
  ): Observable<{
    status: string | null;
    comments: string | null;
    mdrmInProcessReqNo: string | null;
  }> {
    return this.appHttp.post(Config.urls.JSW_GATEWAY_CR_STATUS, { stgNo }).pipe(
      map((resp: unknown) => {
        const data = (resp as any)?.data ?? resp;
        const arr = data && Array.isArray((data as any).apiDataArray) ? (data as any).apiDataArray : [];
        const first = arr[0] as Record<string, unknown> | undefined;
        const statusRaw = first ? first["REQ_STATUS"] : null;
        const status =
          statusRaw != null && String(statusRaw).trim() !== ""
            ? String(statusRaw).trim()
            : null;
        const commentsRaw = first ? first["COMMENTS"] : null;
        const comments =
          commentsRaw != null && String(commentsRaw).trim() !== ""
            ? String(commentsRaw).trim()
            : null;
        const mdrmRaw = first ? first["MDRM_CHANGE_REQ_NUMBER"] : null;
        const mdrmInProcessReqNo =
          mdrmRaw != null && String(mdrmRaw).trim() !== ""
            ? String(mdrmRaw).trim()
            : null;
        return { status, comments, mdrmInProcessReqNo };
      }),
      catchError((err) => {
        if (this.isJswAuthError(err)) { this.markJswAuthFailed(); }
        throw err;
      }),
    );
  }

  /**
   * Refreshes the persisted status for every CR that is still pending (Mongo
   * status not REQUEST ACCEPTED BY ERP SUCESSFULLY and not REJECTED) for the given vendor before the
   * table is rendered.
   *
   * Flow:
   *  1. Page through `jswVMD/pending` until all pending CR ids are collected.
   *  2. If any are pending, get an access token (cached or fresh).
   *  3. For each id sequentially (`concatMap`), call the JSW status API. A
   *     401/403 invalidates the token, re-auths once, and retries that row.
   *  4. When a status is returned, write it and optional JSW COMMENTS back to Mongo.
   *
   * Per-row errors are swallowed (logged) so a single failing CR never blocks
   * the rest of the refresh or the subsequent `/list` call.
   */
  refreshChangeRequestStatuses(vendorCode: string): Observable<void> {
    return this.fetchAllPendingChangeRequestRows(vendorCode).pipe(
      switchMap((rows) => {
        if (!rows || rows.length === 0) {
          return of(void 0);
        }
        return from(rows).pipe(
          concatMap((row) => this.syncStatusForRow(row._id)),
          toArray(),
          map(() => void 0),
        );
      }),
      catchError((err) => {
        console.error("JSW status refresh failed", err);
        return of(void 0);
      }),
    );
  }

  /**
   * Per-row status sync used by `refreshChangeRequestStatuses`. On 401/403
   * from backend, flips `jswAuthFailed$` for the banner. Once the flag is set,
   * subsequent rows short-circuit so we don't hammer the backend.
   *
   * Non-auth failures (5xx, network) for a single row are swallowed (logged)
   * so they don't poison the rest of the loop.
   */
  private syncStatusForRow(stgNo: string): Observable<unknown> {
    if (this.isJswAuthFailed) {
      return of(null);
    }
    return this.fetchChangeRequestStatusFromJsw(stgNo).pipe(
      catchError((err) => {
        console.error(`JSW status fetch failed for ${stgNo}`, err);
        return of(null);
      }),
      switchMap((row) => {
        if (!row || !row.status) {
          return of(null);
        }
        return this.updateChangeRequestStatusInDb(
          stgNo,
          row.status,
          row.comments,
          row.mdrmInProcessReqNo,
        ).pipe(
          catchError((err) => {
            console.error(
              `Failed to persist refreshed status for ${stgNo}`,
              err,
            );
            return of(null);
          }),
        );
      }),
    );
  }

  private mirrorChangeRequest(
    stgNo: string,
    body: Record<string, unknown>,
    jswResponse: unknown,
    updatedFields: Record<string, unknown>,
    uploadedDocumentNames: string[],
  ): Observable<boolean> {
    return this.appHttp
      .post(Config.urls.SUBMIT_CHANGE_REQUEST, {
        stgNo,
        body,
        jswResponse,
        updatedFields,
        uploadedDocumentNames,
      })
      .pipe(
        map(() => true),
        catchError((err) => {
          console.error(
            "Failed to mirror change request to jswVMDQueries",
            err,
          );
          this.dialogs.showErrorModal(
            "The change request was submitted to JSW, but the audit record could not be saved. Please notify support.",
          );
          return of(false);
        }),
      );
  }

  private buildUpdatePayload(
    stgNo: string,
    payload: SubmitChangeRequestPayload,
  ): Record<string, unknown> {
    const { vendorRow, requestorRow, stgColumn691, submitMode } = payload;
    const isUpdate = submitMode === "update";
    const apiReqId = isUpdate
      ? Config.jswVmdApiReqIds.API_REQ_ID_UPDATE_EDIT
      : Config.jswVmdApiReqIds.API_REQ_ID_UPDATE;

    const requestorId = this.resolveRequestorId(requestorRow);
    if (!requestorId) {
      console.warn(
        "JswVmdService: REQUESTOR_ID missing on selected requestor; apiReqUserId / CREATE_BY / EDIT_BY will be empty",
      );
    }
    const apiReqUserId = requestorId;
    const recordNo = this.firstNonEmpty(
      vendorRow["RECORD_NO"],
      vendorRow["MDRM_RECORD_NO"],
      requestorRow["RECORD_NO"],
      requestorRow["MDRM_RECORD_NO"],
    );
    const erpNo = this.firstNonEmpty(
      vendorRow["ERP_NO"],
      vendorRow["SAP_VENDOR_CODE"],
      (localStorage.getItem("vendorCode") || "").trim(),
    );
    const companyCde = this.firstNonEmpty(
      vendorRow["COMPANY_CDE"],
      requestorRow["COMPANY_CDE"],
      "1000",
    );
    const purchaseOrg = this.firstNonEmpty(
      vendorRow["PURCHASE_ORG"],
      requestorRow["PURCHASE_ORG"],
      "P530",
    );

    const statusAndComments = isUpdate
      ? JswVmdService.CR_STATUS_RESUBMITTED_BY_VENDOR
      : JswVmdService.CR_STATUS_SUBMITTED_BY_VENDOR;

    const record: Record<string, unknown> = {
      STG_NO: stgNo,
      RECORD_NO: recordNo,
      ERP_NO: erpNo,
      COMPANY_CDE: companyCde,
      PURCHASE_ORG: purchaseOrg,
      CREATE_BY: requestorId,
      EDIT_BY: requestorId,
      ORGN_ID: JswVmdService.DEFAULT_ORGN_ID,
      STG_COMMENTS: statusAndComments,
      STATUS: statusAndComments,
      STG_TYPE: "VENDXREQUEST",
      DOMAIN: "VENDOR",
      SOURCE: "CHANGE",
      STG_COLUMN691: stgColumn691,
    };

    const root: Record<string, unknown> = {
      apiReqId,
      apiReqOrgnId: JswVmdService.DEFAULT_ORGN_ID,
      apiReqUserId,
      [apiReqId]: [record],
    };
    if (isUpdate) {
      root["apiUpdateFlag"] = "Y";
    } else {
      root["apiInsertFlag"] = "Y";
    }
    return root;
  }

  /** Value from the selected requestor row's Requestor Id (`REQUESTOR_ID`) field. */
  private resolveRequestorId(requestorRow: Record<string, unknown>): string {
    const raw = requestorRow["REQUESTOR_ID"];
    if (raw == null) {
      return "";
    }
    return String(raw).trim();
  }

  private firstNonEmpty(...values: unknown[]): string {
    for (const v of values) {
      if (v != null && String(v).trim() !== "") {
        return String(v).trim();
      }
    }
    return "";
  }

  /**
   * Produces a unique CR id: `VENDX-` + first 16 hex chars of SHA-256(
   * `${Date.now()}-${randomSuffix}` ) where `randomSuffix` is the same 12-char
   * base36 string as the legacy generator. If Web Crypto digest is unavailable
   * or fails, falls back to `VENDX-${randomSuffix}` (legacy shape).
   */
  private async generateStgNo(): Promise<string> {
    const suffix = this.buildRandomStgSuffix();
    const material = `${Date.now()}-${suffix}`;
    const subtle =
      typeof window !== "undefined" && window.crypto && window.crypto.subtle;
    if (!subtle || typeof subtle.digest !== "function") {
      console.warn(
        "JswVmdService: crypto.subtle unavailable; STG_NO uses legacy random-only shape.",
      );
      return `${JswVmdService.STG_NO_PREFIX}${suffix}`;
    }
    try {
      const digest = await subtle.digest(
        "SHA-256",
        new TextEncoder().encode(material),
      );
      const hex = this.bytesToHex(digest).slice(0, 16).toUpperCase();
      return `${JswVmdService.STG_NO_PREFIX}${hex}`;
    } catch (e) {
      console.warn(
        "JswVmdService: SHA-256 digest failed; STG_NO falls back to legacy shape.",
        e,
      );
      return `${JswVmdService.STG_NO_PREFIX}${suffix}`;
    }
  }

  /** 12 base36 chars (same alphabet/length as the pre-hash STG_NO suffix). */
  private buildRandomStgSuffix(): string {
    const alphabet = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const len = 12;
    const cryptoRef =
      typeof window !== "undefined" ? window.crypto : undefined;
    let suffix = "";
    if (cryptoRef && typeof cryptoRef.getRandomValues === "function") {
      const buf = new Uint32Array(len);
      cryptoRef.getRandomValues(buf);
      for (let i = 0; i < len; i++) {
        suffix += alphabet[buf[i] % alphabet.length];
      }
    } else {
      for (let i = 0; i < len; i++) {
        suffix += alphabet[Math.floor(Math.random() * alphabet.length)];
      }
    }
    return suffix;
  }

  private bytesToHex(buffer: ArrayBuffer): string {
    return Array.from(new Uint8Array(buffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  }
}
